import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Calendar, Save, CheckCircle2, Coffee } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';

export default function AdminWorkingHoursPage() {
  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // New holiday form
  const [newHoliday, setNewHoliday] = useState({ date: '', reason: '' });

  const dayNames = [
    'Yakshanba (Sunday)',
    'Dushanba (Monday)',
    'Seshanba (Tuesday)',
    'Chorshanba (Wednesday)',
    'Payshanba (Thursday)',
    'Juma (Friday)',
    'Shanba (Saturday)'
  ];

  // Initialize empty weekly schedule
  const initDefaultDays = () => [
    { dayOfWeek: 0, isOpen: false, openTime: '09:00', closeTime: '18:00', breakStart: '', breakEnd: '' },
    { dayOfWeek: 1, isOpen: true, openTime: '09:00', closeTime: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    { dayOfWeek: 2, isOpen: true, openTime: '09:00', closeTime: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    { dayOfWeek: 3, isOpen: true, openTime: '09:00', closeTime: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    { dayOfWeek: 4, isOpen: true, openTime: '09:00', closeTime: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    { dayOfWeek: 5, isOpen: true, openTime: '09:00', closeTime: '18:00', breakStart: '13:00', breakEnd: '14:00' },
    { dayOfWeek: 6, isOpen: false, openTime: '09:00', closeTime: '18:00', breakStart: '', breakEnd: '' },
  ];

  useEffect(() => {
    Promise.all([api.get('/offices'), api.get('/working-hours/holidays')])
      .then(([oRes, hRes]) => {
        if (oRes?.success && oRes.offices?.length > 0) {
          setOffices(oRes.offices);
          setSelectedOfficeId(String(oRes.offices[0].id));
        }
        if (hRes?.success) setHolidays(hRes.holidays || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  const loadOfficeSchedule = (officeId) => {
    if (!officeId) return;
    setLoading(true);
    api.get(`/working-hours?officeId=${officeId}`)
      .then((res) => {
        if (res?.success && res.workingHours?.length > 0) {
          // Merge with default 7 days
          const days = initDefaultDays();
          res.workingHours.forEach((wh) => {
            const idx = days.findIndex((d) => d.dayOfWeek === wh.dayOfWeek);
            if (idx !== -1) {
              days[idx] = {
                ...days[idx],
                isOpen: wh.isOpen,
                openTime: wh.openTime,
                closeTime: wh.closeTime,
                breakStart: wh.breakStart || '',
                breakEnd: wh.breakEnd || '',
              };
            }
          });
          setSchedule(days);
        } else {
          setSchedule(initDefaultDays());
        }
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedOfficeId) {
      loadOfficeSchedule(selectedOfficeId);
    }
  }, [selectedOfficeId]);

  const handleDayChange = (index, field, value) => {
    setSchedule((prev) => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        [field]: value,
        ...(field === 'isOpen' ? { isWorking: Boolean(value) } : {}),
        ...(field === 'isWorking' ? { isOpen: Boolean(value) } : {}),
      };
      return updated;
    });
  };

  const handleSaveSchedule = async () => {
    if (!selectedOfficeId) {
      showToast('Ofis tanlanmagan', 'error');
      return;
    }
    setSaving(true);
    try {
      const formattedDays = schedule.map((d) => ({
        dayOfWeek: d.dayOfWeek,
        isOpen: Boolean(d.isOpen),
        isWorking: Boolean(d.isOpen),
        openTime: d.openTime || '09:00',
        closeTime: d.closeTime || '18:00',
        breakStart: d.breakStart || null,
        breakEnd: d.breakEnd || null,
      }));

      const res = await api.put('/working-hours/batch', {
        officeId: parseInt(selectedOfficeId, 10),
        days: formattedDays,
      });
      if (res?.success) {
        showToast('Ish vaqtlari muvaffaqiyatli saqlandi!', 'success');
        loadOfficeSchedule(selectedOfficeId);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date || !newHoliday.reason) {
      showToast('Sana va sababni kiriting', 'error');
      return;
    }

    try {
      const res = await api.post('/working-hours/holidays', newHoliday);
      if (res?.success) {
        showToast('Dam olish kuni qo‘shildi', 'success');
        setHolidays([...holidays, res.holiday]);
        setNewHoliday({ date: '', reason: '' });
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const res = await api.delete(`/working-hours/holidays/${id}`);
      if (res?.success) {
        showToast('Dam olish kuni o‘chirildi', 'success');
        setHolidays(holidays.filter((h) => h.id !== id));
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* 1. Working Hours by Office */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs p-3.5 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 border-b border-slate-200 pb-4">
          <div>
            <h2 className="font-serif text-base sm:text-lg font-bold text-slate-900">
              Ofislar Bo‘yicha Haftalik Ish Jadvali
            </h2>
            <p className="text-xs text-slate-500">
              Qabul soatlari va tushlik tanaffuslari booking tizimi tomonidan avtomatik inobatga olinadi.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <select
              value={selectedOfficeId}
              onChange={(e) => setSelectedOfficeId(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-slate-800 font-semibold focus:outline-none flex-1 sm:flex-none"
            >
              {offices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>

            <button
              onClick={handleSaveSchedule}
              disabled={saving}
              className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors cursor-pointer w-full sm:w-auto"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Saqlanmoqda...' : 'Jadvalni Saqlash'}
            </button>
          </div>
        </div>

        {/* Schedule Table */}
        <div className="overflow-x-auto min-w-full">
          <table className="w-full text-left text-xs min-w-[700px]">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase text-[10px]">
              <tr>
                <th className="py-2.5 px-3">Hafta Kuni</th>
                <th className="py-2.5 px-3">Qabul Holati</th>
                <th className="py-2.5 px-3">Boshlanish Vaqti</th>
                <th className="py-2.5 px-3">Tugash Vaqti</th>
                <th className="py-2.5 px-3">Tushlik Boshlanishi</th>
                <th className="py-2.5 px-3">Tushlik Tugashi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {schedule.map((day, idx) => (
                <tr key={day.dayOfWeek} className="hover:bg-slate-50">
                  <td className="py-3 px-3 font-semibold text-slate-900 whitespace-nowrap">
                    {dayNames[day.dayOfWeek]}
                  </td>
                  <td className="py-3 px-3">
                    <div className="inline-flex items-center p-1 bg-slate-100/90 rounded-lg border border-slate-200/90 gap-1">
                      {/* Ish kuni alohida knopka */}
                      <button
                        type="button"
                        onClick={() => {
                          handleDayChange(idx, 'isOpen', true);
                          if (!day.openTime || day.openTime === '--:--') handleDayChange(idx, 'openTime', '09:00');
                          if (!day.closeTime || day.closeTime === '--:--') handleDayChange(idx, 'closeTime', '18:00');
                        }}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 cursor-pointer flex items-center ${
                          day.isOpen
                            ? 'bg-[#091322] text-[#f7f5f0] shadow-xs ring-1 ring-[#091322]/50'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                        }`}
                        title="Ushbu kunni ish kuni qilib belgilash"
                      >
                        <CheckCircle2 className={`w-3.5 h-3.5 mr-1.5 ${day.isOpen ? 'text-[#c59b27]' : 'text-slate-400'}`} />
                        Ish kuni (Ochiq)
                      </button>

                      {/* Dam olish alohida knopka */}
                      <button
                        type="button"
                        onClick={() => handleDayChange(idx, 'isOpen', false)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 cursor-pointer flex items-center ${
                          !day.isOpen
                            ? 'bg-slate-800 text-slate-100 shadow-xs ring-1 ring-slate-700/60'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                        }`}
                        title="Ushbu kunni dam olish kuni qilib belgilash"
                      >
                        <Coffee className={`w-3.5 h-3.5 mr-1.5 ${!day.isOpen ? 'text-amber-300' : 'text-slate-400'}`} />
                        Dam olish
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="time"
                      disabled={!day.isOpen}
                      value={day.openTime}
                      onChange={(e) => handleDayChange(idx, 'openTime', e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-mono text-slate-800 focus:outline-none focus:border-[#091322] focus:ring-1 focus:ring-[#091322] disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 transition-colors"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="time"
                      disabled={!day.isOpen}
                      value={day.closeTime}
                      onChange={(e) => handleDayChange(idx, 'closeTime', e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-mono text-slate-800 focus:outline-none focus:border-[#091322] focus:ring-1 focus:ring-[#091322] disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 transition-colors"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="time"
                      disabled={!day.isOpen}
                      value={day.breakStart}
                      onChange={(e) => handleDayChange(idx, 'breakStart', e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-mono text-slate-800 focus:outline-none focus:border-[#091322] focus:ring-1 focus:ring-[#091322] disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 transition-colors"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="time"
                      disabled={!day.isOpen}
                      value={day.breakEnd}
                      onChange={(e) => handleDayChange(idx, 'breakEnd', e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-mono text-slate-800 focus:outline-none focus:border-[#091322] focus:ring-1 focus:ring-[#091322] disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 transition-colors"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2. Holidays & Blackout Dates */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs p-3.5 sm:p-6 space-y-4 sm:space-y-6">
        <div>
          <h2 className="font-serif text-base sm:text-lg font-bold text-slate-900">
            Bayramlar va Qabulsiz Kunlar (Holidays)
          </h2>
          <p className="text-xs text-slate-500">
            Ushbu kunlar uchun mijozlar bron qila olmaydi.
          </p>
        </div>

        {/* Add Holiday Form */}
        <form onSubmit={handleAddHoliday} className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3 p-3.5 sm:p-4 bg-slate-50 rounded border border-slate-200">
          <div>
            <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Sana</label>
            <input
              type="date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              className="w-full sm:w-auto px-3 py-1.5 border rounded text-xs bg-white"
              required
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Sabab / Tavsif</label>
            <input
              type="text"
              placeholder="Masalan: Mustaqillik kuni munosabati bilan dam olish"
              value={newHoliday.reason}
              onChange={(e) => setNewHoliday({ ...newHoliday, reason: e.target.value })}
              className="w-full px-3 py-1.5 border rounded text-xs bg-white"
              required
            />
          </div>

          <button
            type="submit"
            className="px-4 py-2 bg-[#091322] text-[#c5a880] text-xs font-semibold rounded hover:bg-[#11223b] transition-colors cursor-pointer text-center"
          >
            Kiritish
          </button>
        </form>

        {/* Holidays List */}
        <div className="divide-y divide-slate-100 border border-slate-200 rounded overflow-hidden text-xs">
          {holidays.length === 0 ? (
            <div className="p-4 text-center text-slate-400">Hech qanday maxsus dam olish kuni belgilanmagan</div>
          ) : (
            holidays.map((h) => (
              <div key={h.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-4 h-4 text-[#b8935a]" />
                  <span className="font-mono font-bold text-slate-800">{h.date}</span>
                  <span className="text-slate-600">— {h.reason}</span>
                </div>
                <button
                  onClick={() => handleDeleteHoliday(h.id)}
                  className="p-1 text-rose-500 hover:text-rose-700"
                  title="O‘chirish"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
